'use client';

import { useIsMobile } from '@/hooks/use-mobile';

interface PhotoEventHeroProps {
  eventName: string;
  /** Small uppercase label above the title: what this page is. */
  eyebrow: string;
  /** One line under the title. */
  subtitle?: string;
  /** Optional pill under that: price, date, venue. */
  badge?: string;
  coverImageUrl?: string;
  mobileCoverImageUrl?: string;
  /** Falls back to a themed gradient when the organiser has set no banner. */
  primary: string;
  secondary: string;
}

/**
 * The banner at the top of every photo page.
 *
 * Extracted so the photographer's upload page and the athlete's photo page are
 * the same object rather than two things that resemble each other. They had
 * drifted: one was a full-bleed band with its text on darkened pills, the other
 * a rounded card with text laid straight onto a busy photograph, which was
 * unreadable the moment the banner had people in it.
 *
 * The pills are the part that matters. An organiser's banner is a photograph
 * chosen to look good, not to be typed on, so the text carries its own
 * background rather than trusting the image underneath it.
 */
export function PhotoEventHero({
  eventName, eyebrow, subtitle, badge, coverImageUrl, mobileCoverImageUrl, primary, secondary,
}: PhotoEventHeroProps) {
  const isMobile = useIsMobile();
  // A phone takes a much tighter slice of this band, so an organiser can supply
  // a crop composed for it. CSS picks it, so there is no layout shift and no
  // client-side measuring.
  const image = (isMobile && mobileCoverImageUrl) || coverImageUrl;

  return (
    <div style={{
      position: 'relative',
      background: image
        ? `url(${image}) center/cover no-repeat`
        : `linear-gradient(160deg, ${primary} 0%, ${secondary} 50%, #111 100%)`,
      minHeight: '220px',
      display: 'flex',
      alignItems: 'flex-end',
      overflow: 'hidden',
    }}>
      <div aria-hidden="true" style={{
        position: 'absolute',
        inset: 0,
        background: image
          ? 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.7) 100%)'
          : 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)',
      }} />

      <div style={{ position: 'relative', zIndex: 1, width: '100%', padding: '32px 24px 28px', textAlign: 'center', color: '#fff' }}>
        <span style={{
          display: 'block', width: 'fit-content', margin: '0 auto 12px',
          padding: '4px 14px', borderRadius: '9999px',
          background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)',
          fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase',
        }}>
          {eyebrow}
        </span>

        <h1 style={{
          display: 'inline-block',
          fontSize: '22px', fontWeight: 700, margin: '0 0 8px',
          letterSpacing: '-0.2px', lineHeight: 1.3,
          padding: '6px 20px', borderRadius: '9999px',
          background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
        }}>
          {eventName}
        </h1>

        {subtitle && (
          <p style={{ fontSize: '15px', opacity: 0.9, margin: '0 0 10px', fontWeight: 400 }}>
            {subtitle}
          </p>
        )}

        {badge && (
          <span style={{
            display: 'inline-block', padding: '5px 16px', borderRadius: '9999px',
            background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)',
            fontSize: '13px', fontWeight: 600,
          }}>
            {badge}
          </span>
        )}
      </div>
    </div>
  );
}
